import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Circle, Rocket, X, PartyPopper, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivationChecklist() {
    const { session, profile } = useAuth();
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem('av_activation_dismissed') === '1'; } catch { return false; }
    });

    useEffect(() => {
        if (!session || profile?.is_super_admin || dismissed) return;
        axios.get(`${API}/activation/checklist`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
        }).then(r => setData(r.data)).catch(() => { });
    }, [session, profile, dismissed]);

    if (dismissed || profile?.is_super_admin || !data) return null;

    const dismiss = () => {
        try { localStorage.setItem('av_activation_dismissed', '1'); } catch { /* ignore */ }
        setDismissed(true);
    };

    if (data.completed) {
        return (
            <div className="card-soft p-4 flex items-center justify-between gap-3 border-emerald-300 bg-emerald-50/60"
                data-testid="activation-complete">
                <div className="flex items-center gap-3">
                    <PartyPopper size={20} className="text-emerald-600" />
                    <div>
                        <div className="font-heading text-base">🎉 Compte 100 % activé — bravo !</div>
                        <div className="text-xs text-muted-foreground">Vous maîtrisez les fondamentaux de la plateforme.</div>
                    </div>
                </div>
                <button onClick={dismiss} className="text-muted-foreground hover:text-foreground" aria-label="Fermer"
                    data-testid="activation-dismiss">
                    <X size={16} />
                </button>
            </div>
        );
    }

    const nextStep = data.steps.find(s => !s.done);

    return (
        <div className="card-soft p-5" data-testid="activation-checklist">
            <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <Rocket size={17} className="text-primary" />
                    <h3 className="font-heading text-lg">Bien démarrer — {data.done}/{data.total} étapes</h3>
                </div>
                <button onClick={dismiss} className="text-muted-foreground hover:text-foreground" aria-label="Masquer"
                    data-testid="activation-dismiss">
                    <X size={15} />
                </button>
            </div>
            <div className="h-2 rounded-full bg-surface-muted overflow-hidden mb-4" aria-hidden="true">
                <div className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${data.percent}%` }} />
            </div>
            {nextStep && (
                <div className="mb-4 flex items-center justify-between gap-4 flex-wrap rounded-xl border border-primary/30 bg-primary-light/40 px-4 py-3"
                    data-testid="activation-next-step">
                    <div className="flex items-center gap-3">
                        <span className="h-7 w-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">
                            {data.done + 1}
                        </span>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-primary font-semibold">Votre prochaine étape</div>
                            <div className="text-sm font-medium">{nextStep.label}</div>
                        </div>
                    </div>
                    <Link to={nextStep.link}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary hover:bg-primary-hover text-primary-foreground text-sm font-medium transition-colors"
                        data-testid="activation-next-cta">
                        C'est parti <ArrowRight size={14} />
                    </Link>
                </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {data.steps.map(s => (
                    <Link key={s.key} to={s.link}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-xs transition-colors ${s.done
                            ? 'border-emerald-200 bg-emerald-50/60 text-emerald-800'
                            : 'border-border hover:border-primary/50 hover:bg-surface-muted/50 text-foreground/80'}`}
                        data-testid={`activation-step-${s.key}`}>
                        {s.done
                            ? <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />
                            : <Circle size={15} className="text-muted-foreground shrink-0" />}
                        <span className={s.done ? 'line-through opacity-70' : ''}>{s.label}</span>
                    </Link>
                ))}
            </div>
        </div>
    );
}
